import { Alert, Button, Card, Checkbox, Descriptions, Space, Typography } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { exitKeys, useStudentExits, type ExitChecklistItem, type StudentExit } from "../app/exits.ts";
import { useReportError } from "../app/students.ts";
import { ItemList } from "./ItemList.tsx";

/** The student's exit records with their checklists, newest first; nothing is shown when the student never left. */
export function StudentExitPanel({ studentId, canManage }: { studentId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const exits = useStudentExits(studentId);

  if (exits.isError) {
    return <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void exits.refetch()}>{t("app.retry")}</Button>} />;
  }
  if (exits.isPending || exits.data.length === 0) return null;
  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%", maxWidth: 640 }}>
      {exits.data.map((exit) => (
        <ExitCard key={exit.id} exit={exit} canManage={canManage} />
      ))}
    </Space>
  );
}

function ExitCard({ exit, canManage }: { exit: StudentExit; canManage: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const reportError = useReportError();

  const setItem = useMutation({
    mutationFn: async ({ item, done }: { item: ExitChecklistItem; done: boolean }) => {
      const { data, error, response } = await api.PUT("/students/{id}/exits/{exitId}/items/{itemId}", {
        params: { path: { id: exit.studentId, exitId: exit.id, itemId: item.id } },
        body: { done },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData<StudentExit[]>(exitKeys.exits(exit.studentId), (old) => old?.map((e) => (e.id === updated.id ? updated : e)));
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: reportError,
  });

  const doneCount = exit.items.filter((i) => i.done).length;
  return (
    <Card size="small" title={t("exit.cardTitle", { date: dayjs(exit.leftOn).format("DD.MM.YYYY") })} extra={t("exit.progress", { done: doneCount, total: exit.items.length })}>
      <Descriptions column={1} size="small" styles={{ label: { width: 140 } }} style={{ marginBottom: 12 }}>
        <Descriptions.Item label={t("exit.reason")}>{exit.reasonName}</Descriptions.Item>
        {exit.note && <Descriptions.Item label={t("exit.note")}>{exit.note}</Descriptions.Item>}
      </Descriptions>
      <Typography.Text strong>{t("exit.checklist")}</Typography.Text>
      <ItemList<ExitChecklistItem>
        size="small"
        items={exit.items}
        itemKey={(item) => item.id}
        renderItem={(item) => ({
          content: (
            <Space orientation="vertical" size={0}>
              <Checkbox
                checked={item.done}
                disabled={!canManage || item.isAutomatic || setItem.isPending}
                onChange={(e) => setItem.mutate({ item, done: e.target.checked })}
              >
                {t(`exit.items.${item.kind}`)}
              </Checkbox>
              <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 24 }}>
                {completionText(item, t)}
              </Typography.Text>
            </Space>
          ),
        })}
      />
    </Card>
  );
}

function completionText(item: ExitChecklistItem, t: ReturnType<typeof useTranslation>["t"]): string {
  if (!item.done || !item.completedAt) return item.isAutomatic ? t("exit.automatic") : t("exit.pending");
  const at = dayjs(item.completedAt).format("DD.MM.YYYY HH:mm");
  const who = item.completedBySystem ? t("audit.system") : (item.completedByName ?? t("exit.unknownUser"));
  return t("exit.completedBy", { who, at });
}
